/* eslint-disable @typescript-eslint/no-explicit-any */

import DOMPurify from 'dompurify';

import { extLnkMyPostSafeDrop } from 'config/track-config';
import { EnhancedWindow } from 'models/Window';

export const AppLocation = {
  get href() {
    return window.location.href;
  },
  get search() {
    return window.location.search;
  },
  assign: (url: string | URL) => window.location.assign(url),
  replace: (url: string | URL) => window.location.replace(url),
};

export const AppWindow: EnhancedWindow = window;

// Only get known token from URL
export const getKnownTokenQueryFromUrl = () => {
  const queryParams = new URLSearchParams(AppLocation.search);
  const knownToken = queryParams.get('token') || '';
  const cleanKnownToken = DOMPurify.sanitize(knownToken, { USE_PROFILES: { html: true } });
  const queryString = cleanKnownToken ? `?token=${cleanKnownToken}` : '';
  return queryString;
};

export const reportNewRelicError = (err?: unknown) => {
  const newRelic = AppWindow.newrelic;
  if (newRelic?.noticeError) newRelic.noticeError(err);
};

export const attachNewRelicErrorSuppressions = () => {
  const tryRegisterErrorHandler = () => {
    const newRelicAgent = AppWindow.newrelic;

    // If the agent or setErrorHandler isn't available yet then we can't attach.
    if (!newRelicAgent || typeof newRelicAgent.setErrorHandler !== 'function') {
      return false;
    }

    newRelicAgent.setErrorHandler((error: any) => {
      const message = error?.message || '';
      const stack = error?.stack || '';

      // Check for Fullstop healthcheck 403 error
      // which is thrown for international users
      // by chatbot script
      const is403 = message.includes('403') || message.toLowerCase().includes('forbidden');

      const isFromFullstopScript = stack.includes('fullstop.ai/js/app.js');

      if (is403 && isFromFullstopScript) {
        return true; // ignore this error
      }
      return false;
    });
    return true;
  };

  // try immediately in case the agent is already loaded
  if (tryRegisterErrorHandler()) return;

  // If not yet loaded, retry for a short period
  let attempts = 0;
  const maxAttempts = 50; // 50 * 100ms = 5 seconds
  const retryIntervalMs = 100;

  const intervalId = window.setInterval(() => {
    attempts += 1;

    if (tryRegisterErrorHandler() || attempts > maxAttempts) {
      window.clearInterval(intervalId);
    }
  }, retryIntervalMs);
};

const CACHE_MAX_AGE = 300000; //5min
export const CACHE_SEARCHED_ARTICLES = 'article-added';

const _getItem = (key: string): { data: any; expireTime: number } | null => {
  try {
    const storedData = window.localStorage.getItem(key);
    if (!storedData) return null;
    const { expireTime, data } = JSON.parse(storedData);
    return { data, expireTime };
  } catch (error) {
    return null;
  }
};

export const setCacheItem = (key: string, value: any, preserveExpireTime = false) => {
  const { expireTime: previousExpireTime } = _getItem(key) ?? {};
  const result = {
    data: value,
    expireTime: Date.now() + CACHE_MAX_AGE,
  };

  if (preserveExpireTime && previousExpireTime) result.expireTime = previousExpireTime;
  window.localStorage.setItem(key, JSON.stringify(result));
};

export const getCacheItem = (key: string) => {
  const { data, expireTime } = _getItem(key) ?? {};
  if (!data) return null;
  if (expireTime && expireTime <= Date.now()) return deleteCacheItem(key);
  return data;
};

export const deleteCacheItem = (key: string) => {
  return window.localStorage.removeItem(key);
};

export const getAtlLink = (trackingNumber: string) => {
  const safeDropUrl = `${extLnkMyPostSafeDrop}/${trackingNumber}`;

  // Known token should take precedence; go to safe drop with known token
  const knownTokenQuery = getKnownTokenQueryFromUrl();
  if (knownTokenQuery) return safeDropUrl + knownTokenQuery;

  return safeDropUrl;
};
